import type { WithAuthComponentProps } from '@/_hocs/with-auth'
import { WithAuth } from '@/_hocs/with-auth'
import { getApplicationColumns } from '@/features/_columns/application-table-columns'
import { DataTableFilter } from '@/features/_components/data-table-filter'
import { DataTablePagination } from '@/features/_components/data-table-pagination'
import { DateRangeFilter } from '@/features/_components/date-range-filter'
import { FiltersBar } from '@/features/_components/filters-bar'
import { InlineSearchbox } from '@/features/_components/inline-searchbox'
import { LoadingDataTable } from '@/features/_components/loading-data-table'
import { PersonsTable } from '@/features/_components/persons-table'
import { Container } from '@/features/_components/ui/container'
import { getStatusOptions } from '@/features/_constants/admission-status'
import { useCompanyContext } from '@/features/_contexts/company-context'
import { useFacilityStore } from '@/features/_stores'
import { ExportTableData } from '@/features/applications/_components/export-table-data'
import { ApplicationStatusSchema, type TApplicationStatus } from '@doutorsim/shared/applications'
import { useSearch } from '@tanstack/react-router'
import { useMemo } from 'react'
import { CompanyHeader } from '../_components/company-header'
import { CompanyHeaderLoading } from '../_components/company-header-loading'

const CompanyPersons = ({ session }: WithAuthComponentProps) => {
  const company = useCompanyContext()
  const userType = session.user.userType
  const isStaff = userType === 'staff'
  const facilityId = useFacilityStore((s) => s.currentFacility?.id)
  const searchParams = useSearch({ strict: false }) as {
    search?: string
    status?: string
    startDate?: string
    endDate?: string
  }

  const search = searchParams?.search ?? ''
  const { startDate, endDate } = searchParams

  const status = useMemo(
    () =>
      searchParams?.status
        ? searchParams.status
            .split(',')
            .filter((s): s is TApplicationStatus => ApplicationStatusSchema.safeParse(s).success)
        : [],
    [searchParams?.status],
  )

  if (!company) {
    const columns = getApplicationColumns({ showFacility: false, userType })
    return (
      <Container className="px-14 py-10 gap-6">
        <CompanyHeaderLoading showBack={isStaff} showTabs={isStaff} />
        <FiltersBar>
          <div className="flex items-center gap-2">
            <InlineSearchbox
              search=""
              placeholder="Busque por CPF, nome, telefone, email..."
              disabled
            />
            <DataTableFilter
              title="Status"
              filterKey="status"
              placeholder="Pesquise por status"
              options={getStatusOptions(userType)}
              disabled
            />
            <DateRangeFilter disabled />
          </div>
        </FiltersBar>
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          <LoadingDataTable columns={columns} />
          <DataTablePagination
            currentPage={1}
            totalPages={1}
            totalCount={0}
            canGoNext={false}
            canGoPrevious={false}
            onNextPage={() => {}}
            onPreviousPage={() => {}}
          />
        </div>
      </Container>
    )
  }

  return (
    <Container className="px-14 py-10 gap-6">
      <CompanyHeader
        company={company}
        backHref={isStaff ? '/companies' : undefined}
        breadcrumbs={
          isStaff
            ? [{ label: 'Empresas', href: '/companies' }, { label: company.name }]
            : [{ label: 'Sua Instituição' }]
        }
        tabs={isStaff ? { showPersons: true, showUsers: true, showSettings: true } : undefined}
      />

      <FiltersBar>
        <div className="flex items-center gap-2">
          <InlineSearchbox search={search} placeholder="Busque por CPF, nome, telefone, email..." />
          <DataTableFilter
            title="Status"
            filterKey="status"
            placeholder="Pesquise por status"
            options={getStatusOptions(userType)}
          />
          <DateRangeFilter />
        </div>
        <ExportTableData
          mode="company"
          facilityId={facilityId}
          companyId={company.id}
          userType={userType}
          search={search}
          status={status}
          startDate={startDate}
          endDate={endDate}
        />
      </FiltersBar>

      <PersonsTable
        mode="company"
        facilityId={facilityId}
        companyId={company.id}
        userType={userType}
        showFacilityColumn={isStaff}
        status={status}
        startDate={startDate}
        endDate={endDate}
        search={search}
      />
    </Container>
  )
}

export default WithAuth(CompanyPersons, { allowedFor: ['staff'] })
